/*
  Tokyo Romance Club — Hero slideshow.
  True simultaneous crossfade (both slides are always in the DOM,
  absolutely stacked; opacity alone drives the transition — see
  sections/trc-hero-slideshow.liquid for the CSS). ~6s autoplay, manual
  arrows/dots, touch swipe, loop. Skips all interactive/autoplay wiring
  in the theme editor, where slide switching is instead driven by
  shopify:block:select so merchants can preview each slide by clicking
  its block in the sidebar. Guards against double-initialization when
  the theme editor re-renders the section after a block/setting change.
*/
(function () {
  if (!window.__trcHeroSlideshowGlobalInit) {
    window.__trcHeroSlideshowGlobalInit = true;

    // Always active (even in the theme editor): clicking a slide block in
    // the sidebar shows that slide as a static preview.
    document.addEventListener('shopify:block:select', function (event) {
      if (!(event.target instanceof HTMLElement)) return;
      var slide = event.target.closest('.hero__slide');
      if (!slide) return;
      var grid = slide.closest('[data-hero-slideshow]');
      if (!grid) return;
      grid.querySelectorAll('.hero__slide').forEach(function (s) {
        s.classList.remove('is-active');
      });
      slide.classList.add('is-active');
    });

    document.addEventListener('shopify:block:deselect', function (event) {
      if (!(event.target instanceof HTMLElement)) return;
      var slide = event.target.closest('.hero__slide');
      if (!slide) return;
      var grid = slide.closest('[data-hero-slideshow]');
      if (!grid) return;
      var first = grid.querySelector('.hero__slide');
      grid.querySelectorAll('.hero__slide').forEach(function (s) {
        s.classList.remove('is-active');
      });
      if (first) first.classList.add('is-active');
    });
  }

  var designMode = document.documentElement.classList.contains('shopify-design-mode');
  if (designMode) return;

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  var AUTOPLAY_MS = 6000;
  var SWIPE_THRESHOLD = 40;

  // Reads the width/height already present on the server-rendered <img>/
  // <source> tags (no need to wait for the image bytes to load) so the
  // media box can reserve the right aspect ratio and avoid layout shift,
  // without hardcoding any image dimensions in Liquid or CSS.
  function setAspectRatioVars(grid, firstSlide) {
    if (!firstSlide) return;
    var img = firstSlide.querySelector('img.hero__media');
    if (!img) return;
    var dw = parseFloat(img.getAttribute('width'));
    var dh = parseFloat(img.getAttribute('height'));
    if (dw && dh) {
      grid.style.setProperty('--hero-aspect-desktop', String(dw / dh));
    }
    var source = firstSlide.querySelector('source[media]');
    var mw = source ? parseFloat(source.getAttribute('width')) : dw;
    var mh = source ? parseFloat(source.getAttribute('height')) : dh;
    if (mw && mh) {
      grid.style.setProperty('--hero-aspect-mobile', String(mw / mh));
    }
  }

  function initGrid(grid) {
    if (grid.dataset.heroSlideshowInit === 'true') return;
    grid.dataset.heroSlideshowInit = 'true';

    var slides = Array.prototype.slice.call(grid.querySelectorAll('.hero__slide'));
    if (slides.length === 0) return;

    setAspectRatioVars(grid, slides[0]);

    var state = { current: 0, timer: null };

    grid.setAttribute('data-js-ready', 'true');
    slides.forEach(function (s, i) {
      s.classList.toggle('is-active', i === 0);
      s.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
    });

    if (slides.length <= 1) return;

    var nav = grid.parentElement ? grid.parentElement.querySelector('[data-hero-slideshow-nav]') : null;
    var prevBtn = nav ? nav.querySelector('[data-hero-prev]') : null;
    var nextBtn = nav ? nav.querySelector('[data-hero-next]') : null;
    var dotButtons = nav ? Array.prototype.slice.call(nav.querySelectorAll('[data-hero-goto]')) : [];

    function updateNav() {
      dotButtons.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === state.current);
        dot.setAttribute('aria-current', i === state.current ? 'true' : 'false');
      });
    }

    function goTo(nextIndex) {
      nextIndex = ((nextIndex % slides.length) + slides.length) % slides.length;
      if (nextIndex === state.current) return;
      var from = slides[state.current];
      var to = slides[nextIndex];

      from.setAttribute('aria-hidden', 'true');
      to.setAttribute('aria-hidden', 'false');

      // Both classes flip in the same tick: the CSS opacity transition on
      // .hero__slide handles the actual crossfade (see stylesheet), so the
      // outgoing and incoming slides genuinely overlap — no blackout.
      from.classList.remove('is-active');
      to.classList.add('is-active');

      state.current = nextIndex;
      updateNav();
    }

    function stopAutoplay() {
      if (state.timer) {
        window.clearInterval(state.timer);
        state.timer = null;
      }
    }

    function startAutoplay() {
      if (reduceMotion) return;
      stopAutoplay();
      state.timer = window.setInterval(function () {
        goTo(state.current + 1);
      }, AUTOPLAY_MS);
    }

    function restartAutoplay() {
      stopAutoplay();
      startAutoplay();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(state.current - 1);
        restartAutoplay();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(state.current + 1);
        restartAutoplay();
      });
    }
    dotButtons.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i);
        restartAutoplay();
      });
    });

    // Touch swipe — passive listeners so vertical scrolling is never blocked.
    var touchStartX = null;
    var touchStartY = null;
    grid.addEventListener(
      'touchstart',
      function (e) {
        touchStartX = e.changedTouches[0].clientX;
        touchStartY = e.changedTouches[0].clientY;
      },
      { passive: true }
    );
    grid.addEventListener(
      'touchend',
      function (e) {
        if (touchStartX === null) return;
        var dx = e.changedTouches[0].clientX - touchStartX;
        var dy = e.changedTouches[0].clientY - touchStartY;
        touchStartX = null;
        touchStartY = null;
        if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
        goTo(dx < 0 ? state.current + 1 : state.current - 1);
        restartAutoplay();
      },
      { passive: true }
    );

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    });

    updateNav();
    startAutoplay();
  }

  function init() {
    document.querySelectorAll('[data-hero-slideshow]').forEach(initGrid);
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
